import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Swords, Home as HomeIcon } from "lucide-react";
import MathKeyboard from "../components/MathKeyboard";
import MathText from "../components/MathText";
import ProtectedQuestion from "../components/ProtectedQuestion";
import AntiCheatPopup from "../components/AntiCheatPopup";
import { useMatchSocket } from "../hooks/useMatchSocket";
import ReconnectingOverlay from "../components/ReconnectingOverlay";
import LeaveConfirmModal from "../components/LeaveConfirmModal";
import MatchTools from "../components/MatchTools";
import IncorrectFlash from "../components/IncorrectFlash";
import useLeaveGuard from "../hooks/useLeaveGuard";
import useAntiCheat from "../hooks/useAntiCheat";
import { toast } from "sonner";

const ACTIVE = new Set(["found", "round", "result", "reconnecting"]);

export default function Battle() {
  const { user } = useAuth();
  const { state } = useLocation();
  const nav = useNavigate();
  const [answer, setAnswer] = useState("");
  const [popup, setPopup] = useState(null); // { violation, message }
  const token = typeof window !== "undefined" ? localStorage.getItem("ba_token") : null;
  const roundStartRef = useRef(Date.now());

  // redirect unauthenticated users
  useEffect(() => { if (!user) nav("/login"); }, [user, nav]);

  const { phase, opponent, round, scores, timeLeft, result, endData, skipState,
          reconnect, wrongPulse, sendAnswer, sendSkip, sendLeaveMatch, sendForfeitRound } =
    useMatchSocket({ enabled: !!user, token, filters: state || {} });

  // Reset the per-round submission timer whenever a new round starts.
  useEffect(() => {
    if (phase === "round") roundStartRef.current = Date.now();
  }, [phase, round?.round]);

  // Anti-cheat: tab-blur during a round forfeits THIS round only (1v1).
  const antiCheat = useAntiCheat({
    enabled: phase === "round",
    roundKey: `${round?.round ?? -1}`,
    onForfeit: (reason) => {
      setPopup({ violation: "forfeit" });
      sendForfeitRound(reason);
    },
  });

  // Surface a popup on the first tab-blur warning of the round.
  useEffect(() => {
    if (antiCheat.warning) setPopup({ violation: "tab_switch" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [antiCheat.warning, antiCheat.blurCount]);

  // Leave guard — only active while a match is in progress
  const leaveGuard = useLeaveGuard({
    enabled: ACTIVE.has(phase),
    onConfirmLeave: () => sendLeaveMatch(),  // Casual 1v1 → instant forfeit
  });

  const onSubmit = (e) => {
    e?.preventDefault();
    if (!answer.trim()) return;
    const elapsed = Date.now() - roundStartRef.current;
    const extras = {};
    if (antiCheat.flagSuspiciousIfFast(answer, elapsed)) extras.suspicious = true;
    if (elapsed > 0) extras.elapsed_ms = elapsed;
    sendAnswer(answer, extras);
    setAnswer("");
  };

  const requestLeave = (path) => leaveGuard.requestLeave(path);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-10 min-h-[80vh]">
      <IncorrectFlash trigger={wrongPulse} />
      <AntiCheatPopup
        visible={!!popup}
        violation={popup?.violation}
        message={popup?.message}
        onDismiss={() => setPopup(null)}
      />
      <AnimatePresence mode="wait">
        {(phase === "connecting" || phase === "queue") && (
          <QueueView key="q" state={state} onCancel={() => nav("/")} />
        )}
        {phase === "found" && (
          <FoundView key="f" user={user} opponent={opponent} onLeave={() => requestLeave("/")} />
        )}
        {(phase === "round" || phase === "result" || phase === "reconnecting") && (
          <RoundView
            key="r"
            phase={phase === "reconnecting" ? "round" : phase}
            round={round}
            scores={scores}
            timeLeft={timeLeft}
            answer={answer}
            setAnswer={setAnswer}
            onSubmit={onSubmit}
            result={result}
            skipState={skipState}
            onSkip={sendSkip}
            onLeave={() => requestLeave("/")}
            antiCheat={antiCheat}
            onPasteViolation={() => setPopup({ violation: "paste" })}
            onCopyViolation={() => setPopup({ violation: "copy" })}
          />
        )}
        {phase === "end" && (
          <EndScreen key="e" endData={endData} user={user} onPlayAgain={() => nav("/casual")} />
        )}
      </AnimatePresence>

      {/* Floating in-game whiteboard + calculator — only during active rounds.
          questionKey resets the whiteboard each new round. */}
      {(phase === "round" || phase === "reconnecting") && (
        <MatchTools questionKey={round?.round} />
      )}

      {/* Reconnect overlay — shown above EVERYTHING during the grace window */}
      <AnimatePresence>
        {reconnect?.active && (
          <ReconnectingOverlay secondsLeft={reconnect.secondsLeft} total={6} />
        )}
      </AnimatePresence>

      {/* "Are you sure you want to leave?" — instant-forfeit warning in casual */}
      <LeaveConfirmModal
        open={leaveGuard.open}
        onCancel={leaveGuard.cancel}
        onConfirm={leaveGuard.confirm}
        title="Are you sure you want to leave the game?"
        body="In Casual 1v1, leaving instantly awards victory to your opponent."
      />
    </div>
  );
}

function QueueView({ state, onCancel }) {
  return (
    <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} className="ba-card p-12 text-center">
      <Loader2 className="w-12 h-12 text-cyan animate-spin mx-auto"/>
      <div className="font-heading font-bold text-2xl mt-6">SEARCHING FOR OPPONENT</div>
      <div className="font-mono text-xs tracking-[0.3em] text-zinc-500 mt-2">{(state?.subject||"ANY")} · {(state?.grade||"ANY")}</div>
      <button className="ba-btn-outline px-6 py-3 mt-8" onClick={onCancel} data-testid="cancel-queue-btn">CANCEL</button>
    </motion.div>
  );
}

function FoundView({ user, opponent, onLeave }) {
  return (
    <motion.div initial={{opacity:0, scale:0.95}} animate={{opacity:1, scale:1}} className="ba-card p-6 sm:p-12 text-center">
      {onLeave && (
        <div className="flex justify-end mb-4">
          <button onClick={onLeave} className="ba-btn-outline px-3 py-1.5 text-xs flex items-center gap-1" data-testid="leave-match-btn-found">
            <HomeIcon size={12} /> LEAVE
          </button>
        </div>
      )}
      <div className="font-mono text-xs tracking-[0.3em] text-cyan">MATCH FOUND</div>
      <div className="mt-4 flex items-center justify-center gap-6">
        <PlayerTag name={user.username} elo={user.elo}/>
        <Swords className="text-pink" size={36}/>
        <PlayerTag name={opponent?.username || "?"} elo={opponent?.elo || "?"}/>
      </div>
    </motion.div>
  );
}

function RoundView({ phase, round, scores, timeLeft, answer, setAnswer, onSubmit, result, skipState, onSkip, onLeave, antiCheat, onPasteViolation, onCopyViolation }) {
  const diff = round?.question?.difficulty;
  return (
    <motion.div initial={{opacity:0}} animate={{opacity:1}} className="space-y-4">
      <Scoreboard scores={scores} round={round?.round} total={round?.total} timeLeft={timeLeft} onLeave={onLeave}/>
      <div className="ba-card p-4 sm:p-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="font-mono text-xs sm:text-[10px] tracking-[0.3em] text-cyan break-words">{round?.question?.subject} · {round?.question?.topic} · {round?.question?.grade}</div>
          {diff && <div className={`font-mono text-[10px] tracking-[0.25em] uppercase px-2 py-1 border whitespace-nowrap ${diff==='hard'?'border-pink-500/40 text-pink':'border-cyan-500/40 text-cyan'}`} data-testid="difficulty-badge">{diff}</div>}
        </div>
        <div className="font-heading font-bold text-2xl sm:text-3xl mt-4 leading-snug max-w-full break-words question-text" data-testid="question-text">
          <ProtectedQuestion>{round?.question?.question}</ProtectedQuestion>
        </div>
        {antiCheat?.warning && phase === "round" && (
          <div
            data-testid="anticheat-warning"
            className="mt-4 px-3 py-2 border border-pink-500/60 bg-pink-500/10 text-pink font-mono text-xs tracking-widest flex items-center justify-between"
          >
            <span>⚠ {antiCheat.warning}</span>
            <button
              onClick={antiCheat.dismissWarning}
              className="text-zinc-400 hover:text-white px-2"
              data-testid="anticheat-dismiss"
            >×</button>
          </div>
        )}
        {phase === "round" ? (
          <div className="mt-6">
            <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <input
                autoFocus
                className="ba-input flex-1 text-base sm:text-lg"
                value={answer}
                onChange={e=>setAnswer(e.target.value)}
                onPaste={(e)=>{ e.preventDefault(); onPasteViolation?.(); toast.error("Pasting is disabled during matches."); }}
                onCopy={(e)=>{ e.preventDefault(); onCopyViolation?.(); }}
                onCut={(e)=>{ e.preventDefault(); onCopyViolation?.(); }}
                onDrop={(e)=>e.preventDefault()}
                autoComplete="off"
                spellCheck="false"
                placeholder="Type your answer..."
                data-testid="answer-input"
              />
              <button type="submit" className="ba-btn-primary px-4 sm:px-6 py-3 whitespace-nowrap" data-testid="submit-answer-btn">SUBMIT</button>
            </form>
            <div className="mt-3 flex items-center justify-between gap-3">
              <MathKeyboard onInsert={(t)=>setAnswer(a=>a+t)}/>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500">
                SKIP VOTES <span className="text-cyan">{skipState?.votes ?? 0}/{skipState?.required ?? 2}</span>
              </div>
              <button type="button" onClick={onSkip} disabled={skipState?.mine}
                className="ba-btn-outline px-4 py-2 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="skip-vote-btn">
                {skipState?.mine ? "VOTED TO SKIP" : "VOTE SKIP"}
              </button>
            </div>
          </div>
        ) : (
          <ResultBanner result={result}/>
        )}
      </div>
    </motion.div>
  );
}

function PlayerTag({ name, elo }) {
  return (
    <div className="text-center">
      <div className="font-heading font-bold text-xl">{name}</div>
      <div className="font-mono text-xs text-cyan tracking-widest mt-1">{elo} MMR</div>
    </div>
  );
}

function Scoreboard({ scores, round, total, timeLeft, onLeave }) {
  const urgent = timeLeft <= 5;
  return (
    <div className="ba-card p-4">
      {onLeave && (
        <div className="flex justify-end mb-2">
          <button
            onClick={onLeave}
            className="ba-btn-outline px-2.5 py-1 text-[10px] font-mono tracking-widest flex items-center gap-1 min-h-0"
            data-testid="leave-match-btn-round"
          >
            <HomeIcon size={11} /> LEAVE
          </button>
        </div>
      )}
      <div className="grid grid-cols-3 items-center gap-2">
        <div className="text-center min-w-0">
          <div className="font-mono text-[10px] tracking-[0.2em] sm:tracking-[0.3em] text-zinc-500 truncate px-1">{scores?.[0]?.username}</div>
          <div className="font-heading font-black text-3xl text-cyan" data-testid="score-p1">{scores?.[0]?.score ?? 0}</div>
        </div>
        <div className="text-center">
          <div className="font-mono text-[10px] tracking-[0.2em] sm:tracking-[0.3em] text-zinc-500">ROUND {round}/{total}</div>
          <div className={`font-heading font-black text-3xl ${urgent ? "text-pink animate-pulse" : "text-white"}`} data-testid="timer">{String(timeLeft).padStart(2,"0")}s</div>
        </div>
        <div className="text-center min-w-0">
          <div className="font-mono text-[10px] tracking-[0.2em] sm:tracking-[0.3em] text-zinc-500 truncate px-1">{scores?.[1]?.username}</div>
          <div className="font-heading font-black text-3xl text-pink" data-testid="score-p2">{scores?.[1]?.score ?? 0}</div>
        </div>
      </div>
    </div>
  );
}

function ResultBanner({ result }) {
  if (!result) return null;
  const reason = result.reason;
  let title = result.winner ? <span className="text-cyan">{result.winner} wins the round</span> : "TIME OUT";
  if (reason === "skipped") title = "ROUND SKIPPED";
  if (reason === "forfeit") {
    title = (
      <span className="text-pink">
        {result.forfeit_by || "Opponent"} forfeited — {result.winner} wins the round
      </span>
    );
  }
  return (
    <motion.div initial={{opacity:0, y:10}} animate={{opacity:1, y:0}} className="mt-6 p-5 border-l-2 border-cyan-400 bg-cyan-400/5">
      <div className="font-heading font-bold text-lg">{title}</div>
      <div className="text-zinc-400 text-sm mt-1">Correct answer: <span className="text-white font-mono"><MathText>{result.correct_answer}</MathText></span></div>
      {result.explanation && <div className="text-zinc-500 text-sm mt-2"><MathText>{result.explanation}</MathText></div>}
    </motion.div>
  );
}

function EndScreen({ endData, user, onPlayAgain }) {
  const reason = endData?.reason;
  const isDisconnect = reason === "connection_lost";
  const isForfeit = endData?.forfeit;
  const me = endData.scores?.find?.((s) => s.username === user.username);
  const opp = endData.scores?.find?.((s) => s.username !== user.username);
  const won = !isDisconnect && endData.winner === user.username;
  const draw = !isDisconnect && !endData.winner;
  let label = "WIN";
  if (isDisconnect) label = "DISCONNECTED";
  else if (draw) label = "STALEMATE";
  else if (!won) label = "LOSS";
  let tagline = "VICTORY";
  if (isDisconnect) tagline = "CONNECTION LOST";
  else if (draw) tagline = "DRAW";
  else if (!won) tagline = "DEFEAT";
  const accent = won ? "text-glow-cyan text-cyan" : "text-glow-pink text-pink";
  const tagColor = won ? "text-win" : "text-pink";

  // Forfeit (opponent left) – clarify message so the player doesn't think they "won early" mistakenly
  const forfeitNote = isForfeit && !isDisconnect && won
    ? `${endData.forfeit_by || "Opponent"} left the match — you win by forfeit.`
    : null;

  return (
    <motion.div initial={{opacity:0, scale:0.95}} animate={{opacity:1, scale:1}} className="ba-card p-10 text-center">
      <div className={`font-mono text-xs tracking-[0.4em] ${tagColor}`} data-testid="end-screen-tagline">{tagline}</div>
      <div className={`font-heading font-black text-5xl sm:text-6xl mt-3 ${accent}`} data-testid="end-screen-title">{label}</div>
      {forfeitNote && (
        <div className="mt-4 text-sm text-zinc-400 font-mono" data-testid="end-screen-forfeit-note">{forfeitNote}</div>
      )}
      {isDisconnect && (
        <div className="mt-4 text-sm text-zinc-400 font-mono" data-testid="end-screen-disconnect-note">
          Your connection dropped. The match has ended for you.
        </div>
      )}
      {!isDisconnect && (
        <div className="mt-8 flex items-center justify-center gap-12">
          <div>
            <div className="font-mono text-xs tracking-widest text-zinc-500">{me?.username || user.username}</div>
            <div className="font-heading font-black text-4xl text-cyan">{me?.score ?? 0}</div>
            <div className={`font-mono text-sm mt-1 ${me?.elo_change>=0?'text-win':'text-pink'}`}>{me?.elo_change>=0?"+":""}{me?.elo_change ?? 0} MMR</div>
          </div>
          <div className="text-zinc-600 font-heading text-2xl">vs</div>
          <div>
            <div className="font-mono text-xs tracking-widest text-zinc-500">{opp?.username || "Opponent"}</div>
            <div className="font-heading font-black text-4xl text-pink">{opp?.score ?? 0}</div>
            <div className="font-mono text-sm text-zinc-500 mt-1">{opp?.elo_change>=0?"+":""}{opp?.elo_change ?? 0} MMR</div>
          </div>
        </div>
      )}
      <div className="mt-10 flex justify-center gap-3">
        <button className="ba-btn-primary px-6 py-3" onClick={onPlayAgain} data-testid="play-again-btn">PLAY AGAIN</button>
        <button className="ba-btn-outline px-6 py-3" onClick={()=>window.location.href="/leaderboard"} data-testid="goto-ladder">LADDER</button>
      </div>
    </motion.div>
  );
}
